import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useStatistics } from '../hooks/useStatistics';
import { SCORM_DOWNLOAD_PRICING } from '../lib/features';
import { icon } from '../components/ui/site-icon';
import { WaitlistForm } from '../components/ui/waitlist-form';

// Homepage below the converter hero, laid out as a SCORM manual: sticky contents, numbered chapters, FAQ.
// Styles live in src/styles/site.css (".manual" block).

export const SUPPORT_EMAIL = 'mailto:scormate@coloris.com.ua';

const CHAPTERS = [
  { id: 'how-to-convert', key: 'ch1' },
  { id: 'slides-vs-course', key: 'ch2' },
  { id: 'ai-builder', key: 'ch3' },
  { id: 'scorm-versions', key: 'ch4' },
  { id: 'pricing', key: 'ch5' }
].filter((chapter) => SCORM_DOWNLOAD_PRICING || chapter.id !== 'pricing');

const FAQ_KEYS = ['Formats', 'Versions', 'Free', 'Commercial', 'Video', 'Lms', 'Time', 'Files', 'Account', 'Pptx', 'Review', 'Price']
  .filter((key) => SCORM_DOWNLOAD_PRICING || key !== 'Price');

/** FAQ entries shared by the page and its FAQPage structured data. */
export function useFaq() {
  const { t } = useTranslation();
  return FAQ_KEYS.map((key) => ({ key, question: t(`site.faq.q${key}`), answer: t(`site.faq.a${key}`) }));
}

const FEED = [
  { role: 'Coordinator', at: '0:00' },
  { role: 'Methodologist', at: '0:06' },
  { role: 'Author', at: '0:41' },
  { role: 'Author', at: '1:37' },
  { role: 'Reviewer', at: '2:12' },
  { role: 'Author', at: '2:58' },
  { role: 'Illustrator', at: '3:30' },
  { role: 'Author', at: '4:05' },
  { role: 'Reviewer', at: '4:47' },
  { role: 'Coordinator', at: '6:20' }
];

export function StatsLedger() {
  const { t, i18n } = useTranslation();
  const { statistics } = useStatistics();
  // The API answers zeros when Redis is down or empty; a ledger of zeros is worse than none.
  if (!statistics?.successfulConversions) return null;
  const number = (value: number, digits = 0) =>
    new Intl.NumberFormat(i18n.language, { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value);
  return (
    <div className="ledger">
      <div className="wrap ledger-inner">
        <span className="ledger-label"><span className="live-dot" aria-hidden="true"></span>{t('site.stats.label')}</span>
        <ul>
          <li>{t('site.stats.conversions', { value: number(statistics.successfulConversions) })}</li>
          <li>{t('site.stats.data', { value: number(statistics.processedDataGB, 1) })}</li>
          <li>{t('site.stats.time', { value: number(statistics.averageConversionTime) })}</li>
          <li>{t('site.stats.success', { value: number(statistics.conversionSuccessRate, 1) })}</li>
        </ul>
      </div>
    </div>
  );
}

/** Course languages available in the builder, by total speakers worldwide; endonyms, so not translated. */
const COURSE_LANGUAGES = ['English', 'Español', 'Français', 'Português', 'Italiano', 'Polski', 'Українська'];

export function HomeManual({ currentLang }: { currentLang: string }) {
  const { t } = useTranslation();
  const faq = useFaq();
  const active = useActiveChapter();
  const m = (key: string) => t(`site.manual.${key}`);
  const numberOf = (id: string) => CHAPTERS.findIndex((chapter) => chapter.id === id) + 1;

  const heading = (id: string, key: string) => (
    <h2><span className="ch-no" aria-hidden="true">{numberOf(id)}</span>{m(`${key}.title`)}</h2>
  );

  return (
    <div className="manual">
      <div className="wrap manual-grid">
        <nav className="manual-toc" aria-label={m('contents')}>
          <strong>{m('contents')}</strong>
          <ol>
            {CHAPTERS.map((chapter, index) => (
              <li key={chapter.id}>
                <a href={`#${chapter.id}`} aria-current={active === chapter.id ? 'true' : undefined}>
                  <span className="toc-no">{index + 1}</span>{m(`${chapter.key}.nav`)}
                </a>
              </li>
            ))}
            <li><a href="#faq" aria-current={active === 'faq' ? 'true' : undefined}><span className="toc-no" aria-hidden="true"></span>{t('site.header.faq')}</a></li>
          </ol>
        </nav>

        <div className="manual-body">
          <section className="chapter" id="how-to-convert" data-chapter>
            {heading('how-to-convert', 'ch1')}
            <p className="chapter-intro">{m('ch1.intro')}</p>
            <dl className="spec">
              {(['Input', 'Output', 'Video', 'Account', 'Lms'] as const).map((key) => (
                <div key={key}><dt>{m(`ch1.spec${key}`)}</dt><dd>{m(`ch1.spec${key}Value`)}</dd></div>
              ))}
            </dl>
            <h3>{m('ch1.stepsTitle')}</h3>
            <ol className="procedure">
              {[1, 2, 3].map((n) => <li key={n}>{m(`ch1.step${n}`)}</li>)}
            </ol>
            <a className="text-link" href="#converter">{m('ch1.back')}{icon('ArrowRight')}</a>
          </section>

          <section className="chapter" id="slides-vs-course" data-chapter>
            {heading('slides-vs-course', 'ch2')}
            <p className="chapter-intro">{m('ch2.intro')}</p>
            <div className="table-scroll">
              <table className="compare">
                <caption className="sr-only">{m('ch2.tableCaption')}</caption>
                <thead>
                  <tr><th scope="col"><span className="sr-only">{m('ch2.colFeature')}</span></th><th scope="col">{m('ch2.colConverter')}</th><th scope="col" className="is-builder">{m('ch2.colBuilder')}</th></tr>
                </thead>
                <tbody>
                  {(['Source', 'Learner', 'SourceCheck', 'Languages', 'Scorm', 'Price'] as const).map((row) => (
                    <tr key={row}>
                      <th scope="row">{m(`ch2.row${row}`)}</th>
                      <td>{m(`ch2.row${row}Converter`)}</td>
                      <td className="is-builder">{m(row === 'Price' && !SCORM_DOWNLOAD_PRICING ? 'ch2.rowPriceBuilderFree' : `ch2.row${row}Builder`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <a className="text-link" href="#ai-builder">{m('ch2.cta')}{icon('ArrowRight')}</a>
          </section>

          <section className="chapter chapter-ai" id="ai-builder" data-chapter>
            {heading('ai-builder', 'ch3')}
            <p className="chapter-intro">{m('ch3.intro')}</p>

            <div className="step">
              <h3><span className="step-no">3.1</span>{m('ch3.s1Title')}</h3>
              <p>{m('ch3.s1Text')}</p>
              <figure className="brief">
                <div className="figure-bar"><span>{m('ch3.briefLabel')}</span><span className="figure-note">{m('ch3.briefNote')}</span></div>
                <dl>
                  {(['Goal', 'Audience', 'Language', 'Duration', 'Style'] as const).map((key) => (
                    <div key={key}><dt>{m(`ch3.brief${key}`)}</dt><dd>{m(`ch3.brief${key}Value`)}</dd></div>
                  ))}
                  <div>
                    <dt>{m('ch3.briefQuestions')}</dt>
                    <dd className="chips">
                      {(['Single', 'Multiple', 'TrueFalse', 'Short', 'Essay'] as const).map((key, index) => (
                        <span key={key} className={index < 3 ? 'chip on' : 'chip'}>{index < 3 && icon('Check')}{m(`ch3.q${key}`)}</span>
                      ))}
                    </dd>
                  </div>
                </dl>
              </figure>
            </div>

            <div className="step">
              <h3><span className="step-no">3.2</span>{m('ch3.s2Title')}</h3>
              <p>{m('ch3.s2Text')}</p>
              <figure className="shot">
                <div className="shot-frame">
                  <div className="figure-bar"><span className="mono">sb.scormate.com/courses/…/plans</span></div>
                  <div className="shot-canvas">
                    <img src="/site/product/plan.webp" alt={m('ch3.planAlt')} width="1285" height="1061" loading="lazy" decoding="async" />
                    {[[3.9, 51.9], [3.9, 66.3], [72, 42.8], [51.5, 96.3]].map(([x, y], index) => (
                      <span key={index} className="pin" style={{ left: `${x}%`, top: `${y}%` }} aria-hidden="true">{index + 1}</span>
                    ))}
                  </div>
                </div>
                <figcaption>
                  <ol className="pin-legend">{[1, 2, 3, 4].map((n) => <li key={n}>{m(`ch3.pin${n}`)}</li>)}</ol>
                </figcaption>
              </figure>
            </div>

            <div className="step">
              <h3><span className="step-no">3.3</span>{m('ch3.s3Title')}</h3>
              <p>{m('ch3.s3Text')}</p>
              <ActivityFeed />
            </div>

            <div className="step">
              <h3><span className="step-no">3.4</span>{m('ch3.s4Title')}</h3>
              <p>{m('ch3.s4Text')}</p>
              <figure className="shot">
                <div className="shot-frame">
                  <div className="figure-bar"><span className="mono">sb.scormate.com/courses/…/editor</span></div>
                  <div className="shot-canvas">
                    <img src="/site/product/editor.webp" alt={m('ch3.editorAlt')} width="1600" height="925" loading="lazy" decoding="async" />
                  </div>
                </div>
                <figcaption>
                  <ul className="point-list">{[1, 2, 3, 4].map((n) => <li key={n}>{icon('Check')}{m(`ch3.s4Point${n}`)}</li>)}</ul>
                </figcaption>
              </figure>
            </div>

            <div className="step">
              <h3><span className="step-no">3.5</span>{m('ch3.s5Title')}</h3>
              <p>{m('ch3.s5Text')}</p>
              <figure className="export">
                <div className="figure-bar"><span>SCORM ZIP</span><span className="figure-note">{m('ch3.exportNote')}</span></div>
                <dl>
                  <div><dt>{m('ch3.exportVersion')}</dt><dd><span className="segmented"><span className="on">SCORM 2004</span><span>SCORM 1.2</span></span></dd></div>
                  <div><dt>{m('ch3.exportScore')}</dt><dd className="mono-value">{m('ch3.exportScoreValue')}</dd></div>
                  <div><dt>{m('ch3.exportAttempts')}</dt><dd className="mono-value">{m('ch3.exportAttemptsValue')}</dd></div>
                  <div><dt>{m('ch3.exportScreens')}</dt><dd><span className="toggle" role="img" aria-label={m('ch3.exportScreensValue')}></span></dd></div>
                </dl>
              </figure>
            </div>

            <aside className="limits">
              <h3>{m('ch3.trustTitle')}</h3>
              <ul>{[1, 2, 3].map((n) => <li key={n}>{m(`ch3.trust${n}`)}</li>)}</ul>
            </aside>

            <div className="waitlist-block" id="waitlist">
              <h3>{t('site.waitlist.title')}</h3>
              <p>{t('site.waitlist.text')}</p>
              <WaitlistForm source="manual" currentLang={currentLang} />
            </div>
          </section>

          <section className="chapter" id="scorm-versions" data-chapter>
            {heading('scorm-versions', 'ch4')}
            <p className="chapter-intro">{m('ch4.intro')}</p>
            <div className="versions">
              {(['v12', 'v2004'] as const).map((key) => (
                <article key={key}>
                  <h3>{m(`ch4.${key}Title`)}</h3>
                  <p>{m(`ch4.${key}Text`)}</p>
                  <p className="where"><span>{m('ch4.whereLabel')}</span>{m(`ch4.${key}Where`)}</p>
                </article>
              ))}
            </div>
            <p className="advice">{m('ch4.advice')}</p>
          </section>

          {SCORM_DOWNLOAD_PRICING && (
            <section className="chapter" id="pricing" data-chapter>
              {heading('pricing', 'ch5')}
              <p className="chapter-intro lead-line">{m('ch5.intro')}</p>
              <table className="price-table">
                <caption className="sr-only">{m('ch5.tableCaption')}</caption>
                <tbody>
                  {([['Converter', 'free'], ['Project', 'free'], ['Export', 'exportPrice'], ['Languages', 'free']] as const).map(([row, price]) => (
                    <tr key={row} className={row === 'Export' ? 'is-paid' : undefined}>
                      <th scope="row">
                        <strong>{m(`ch5.row${row}`)}</strong><span>{m(`ch5.row${row}Note`)}</span>
                        {row === 'Languages' && <ul className="lang-list">{COURSE_LANGUAGES.map((name) => <li key={name}>{name}</li>)}</ul>}
                      </th>
                      <td>{m(`ch5.${price}`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="point-list terms">{[1, 2, 3].map((n) => <li key={n}>{icon('Check')}{m(`ch5.term${n}`)}</li>)}</ul>
              <p className="price-note">{m('ch5.projectNote')} <a className="text-link" href="#waitlist">{t('site.header.earlyAccess')}{icon('ArrowRight')}</a></p>
            </section>
          )}

          <section className="chapter faq" id="faq" data-chapter>
            <h2>{t('site.faq.title')}</h2>
            <div className="faq-list">
              {faq.map((item) => (
                <details key={item.key}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                  {item.key === 'Files' && <Link className="text-button" to={`/${currentLang}/privacy-policy`}>{t('site.faq.privacyLink')}</Link>}
                </details>
              ))}
            </div>
            <p className="faq-contact muted">{t('site.faq.text')} <a className="text-link" href={SUPPORT_EMAIL}>{t('site.faq.contact')}{icon('ArrowRight')}</a></p>
          </section>
        </div>
      </div>

      <section className="final-cta">
        <div className="wrap final-inner">
          <div>
            <h2>{t('site.final.title')}</h2>
            <p>{t('site.final.text')}</p>
          </div>
          <div className="final-actions">
            <WaitlistForm source="final" currentLang={currentLang} />
            <a className="text-link" href="#converter">{t('site.final.converter')}</a>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Chapter whose top most recently crossed the upper third of the viewport. */
function useActiveChapter() {
  const [active, setActive] = useState<string>(CHAPTERS[0].id);
  useEffect(() => {
    const sections = [...document.querySelectorAll<HTMLElement>('[data-chapter]')];
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-20% 0px -70% 0px' });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);
  return active;
}

/** Example activity feed: plays once when scrolled into view, fully shown when motion is reduced. */
function ActivityFeed() {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(0);
  const [run, setRun] = useState(0);
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduced) { setShown(FEED.length); return; }
    const node = ref.current;
    if (!node) return;
    let timer: number | undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      setShown(1);
      timer = window.setInterval(() => setShown((count) => {
        if (count + 1 >= FEED.length) window.clearInterval(timer);
        return Math.min(FEED.length, count + 1);
      }), 900);
    }, { threshold: 0.4 });
    observer.observe(node);
    return () => { observer.disconnect(); window.clearInterval(timer); };
  }, [reduced, run]);

  const done = shown >= FEED.length;
  const current = FEED[Math.max(0, shown - 1)];

  return (
    <figure className="feed" ref={ref}>
      <div className="figure-bar">
        <span className="feed-title">{t('site.manual.ch3.feedLabel')}</span>
        {done && !reduced
          ? <button type="button" className="text-button" onClick={() => { setShown(0); setRun((n) => n + 1); }}>{t('site.manual.ch3.feedReplay')}</button>
          : <span className={`feed-now${done ? ' done' : ''}`}>{shown > 0 && t(`site.manual.ch3.role${current.role}`)}</span>}
      </div>
      <ol aria-live="off">
        {FEED.map((item, index) => (
          <li key={index} className={index < shown ? 'in' : undefined} data-role={item.role.toLowerCase()} aria-hidden={index < shown ? undefined : 'true'}>
            <span className="feed-at">{item.at}</span>
            <span className="feed-role">{t(`site.manual.ch3.role${item.role}`)}</span>
            <span className="feed-text">{t(`site.manual.ch3.feed${index + 1}`)}</span>
          </li>
        ))}
      </ol>
      <figcaption>{t('site.manual.ch3.feedNote')}</figcaption>
    </figure>
  );
}

